/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
/**
 * Automation script for Capital Decrease (Reducción de Capital) modal.
 * Simulates user interactions to create movements for each participant.
 * Stops before "Guardar operación" - user performs manual review before saving.
 *
 * Flow:
 * - Partner: término de búsqueda por participante.
 * - Clase: se selecciona una sola vez al inicio (defaultClassSearchTerm).
 * - Rango: se hace clic en el único rango disponible de cada socio (Rangos disponibles).
 *
 * Usage:
 * 1. Open the Capital Decrease modal
 * 2. (Optional) Fill scripture view and click Next, or run with runScripturePhase: true
 * 3. Run automateDecreaseOperation(participants, config)
 */
import { cleanValue } from 'constants/formats';

const DELAY_RANGES_POLL = 200; // Interval to poll for range cell / dropdown options
const RANGES_POLL_MAX_ATTEMPTS = 25; // Max ~5s waiting for ranges
const DROPDOWN_OPTIONS_POLL_MAX = 25; // Max ~5s waiting for partner search API
const DELAY_STEPS = 200; // Delay between UI steps
const DELAY_AFTER_RANGE_CLICK = 300; // Wait for React state (shareFrom/shareTo) to update
const SAVE_BUTTON_POLL_INTERVAL = 200; // Poll for save button enabled (validation API)
const SAVE_BUTTON_POLL_MAX = 30; // Max ~6s wait for validation API
const VALIDATE_BUTTON_POLL_INTERVAL = 100; // Poll for validate button enabled
const VALIDATE_BUTTON_POLL_MAX = 25; // ~2.5s max wait after range click
const DELAY_INITIAL_BEFORE_POLL = 150; // Brief delay before first poll (let request start)
const DELAY_RANGES_INITIAL = 400; // Wait for ranges API to be triggered after partner selection
const DELAY_SCRIPTURE_AFTER_NEXT = 500; // After clicking Next in scripture phase (view transition)

export type DecreaseParticipant = {
  /** Término de búsqueda del socio (mín. 3 caracteres). Ej: "Juan Pérez", "García" */
  partnerSearchTerm: string;
  /**
   * Nº de participaciones a reducir para este socio.
   * Tras hacer clic en el rango (ej. 1-100), se ajusta shareTo para que
   * shareTo - shareFrom + 1 = sharesToDecrease. Ej: rango 1-100, sharesToDecrease 50 → 1-50.
   */
  sharesToDecrease?: number;
};

export type AutomationLogEntry = {
  msg: string;
  type: 'info' | 'success' | 'error';
  step?: number;
};

type DecreaseConfig = {
  /** Run scripture phase: fill date, total shares, click Next. Default: false */
  runScripturePhase?: boolean;
  /** Date for operation (YYYY-MM-DD). Required if runScripturePhase is true */
  date?: string;
  /** Total shares to decrease. Required if runScripturePhase is true */
  totalSharesToDecrease?: number;
  /**
   * Clase única para todos los participantes. Si se indica, se selecciona una sola vez
   * al inicio y se omite el paso de clase para cada participante.
   */
  defaultClassSearchTerm?: string;
  /** Callback to log each step for debugging. Steps run sequentially - next only after current completes. */
  // eslint-disable-next-line no-unused-vars -- callback param for type definition
  onLog?: (entry: AutomationLogEntry) => void;
  /** Callback when a movement is saved (after Save Movement click). Receives participant and 1-based index. */
  onMovementSaved?: (
    participant: DecreaseParticipant, // eslint-disable-line no-unused-vars
    index: number // eslint-disable-line no-unused-vars
  ) => void;
};

// Helper functions for React-Select / CustomAsyncSelect
const ReactSelectHelpers = {
  findInputById(id: string): HTMLInputElement | null {
    const container = document.getElementById(id);
    if (!container) return null;
    const input = container.querySelector('input');
    return input as HTMLInputElement;
  },

  findAnyReactSelectInput(): HTMLInputElement | null {
    return document.querySelector(
      'input[id^="react-select-"][id$="-input"]'
    ) as HTMLInputElement | null;
  },

  getBaseIdFromElement(element: HTMLElement | null): string | null {
    if (!element?.id) return null;
    const match = element.id.match(/react-select-(\d+)/);
    return match ? match[1] : null;
  },

  findDropdownOption(
    inputElement: HTMLInputElement | null
  ): HTMLElement | null {
    if (!inputElement) return null;
    const baseId = this.getBaseIdFromElement(inputElement);
    if (baseId) {
      const byId = document.querySelector(
        `[id^="react-select-${baseId}-option-"]`
      );
      if (byId) return byId as HTMLElement;
    }
    return document.querySelector('[role="option"]') as HTMLElement | null;
  },

  /** react-select attaches onKeyDown to the control div, not the input */
  getControlElement(input: HTMLInputElement): HTMLElement | null {
    return input.closest('.react-select__control') as HTMLElement | null;
  },

  simulateEnter(target: HTMLElement): void {
    const opts = {
      key: 'Enter',
      code: 'Enter',
      keyCode: 13,
      which: 13,
      bubbles: true,
      cancelable: true,
    };
    const control =
      target instanceof HTMLInputElement
        ? this.getControlElement(target) || target
        : target;
    control.dispatchEvent(new KeyboardEvent('keydown', opts));
  },

  async simulateKeyboardSelection(
    inputElement: HTMLInputElement | null
  ): Promise<boolean> {
    if (!inputElement) return false;
    const control = this.getControlElement(inputElement) || inputElement;
    control.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'ArrowDown',
        code: 'ArrowDown',
        keyCode: 40,
        bubbles: true,
      })
    );
    return new Promise((resolve) => {
      setTimeout(() => {
        ReactSelectHelpers.simulateEnter(inputElement);
        resolve(true);
      }, 100);
    });
  },
};

type LogFn = (
  msg: string, // eslint-disable-line no-unused-vars
  type?: AutomationLogEntry['type'] // eslint-disable-line no-unused-vars
) => void;

/**
 * Sets value on a React controlled input and triggers the input event.
 * React ignores direct .value assignment; we need the native setter.
 */
function setReactInputValue(input: HTMLInputElement, value: string): void {
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    'value'
  )?.set;
  if (nativeInputValueSetter) {
    nativeInputValueSetter.call(input, value);
  } else {
    // eslint-disable-next-line no-param-reassign -- required for React controlled input simulation
    input.value = value;
  }
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

function findPartnerInput(): HTMLInputElement | null {
  const byId =
    document.getElementById('partner-select-input')?.querySelector('input') ||
    (document.querySelector(
      '[id="partner-select-input"] input'
    ) as HTMLInputElement);
  if (byId) return byId;
  return ReactSelectHelpers.findAnyReactSelectInput();
}

function findFirstDropdownOption(): HTMLElement | null {
  return (
    document.querySelector('[role="option"]') ||
    document.querySelector('div[id*="-option-"]')
  ) as HTMLElement | null;
}

/** Polls until condition is true or max attempts. Returns true if condition met. */
async function pollUntil(
  check: () => boolean,
  intervalMs: number,
  maxAttempts: number,
  log?: LogFn,
  label?: string
): Promise<boolean> {
  for (let i = 0; i < maxAttempts; i += 1) {
    if (check()) return true;
    if (i > 0 && log && label) {
      log?.(`${label} (${i + 1}/${maxAttempts})...`, 'info');
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return false;
}

async function typeInPartnerInput(
  partnerSearchTerm: string,
  log?: LogFn
): Promise<void> {
  log?.('Typing in partner input...', 'info');
  const input = findPartnerInput();

  if (!input) {
    log?.('ERROR: Partner input element not found', 'error');
    throw new Error('Partner input element not found');
  }

  input.focus();
  setReactInputValue(input, '');
  setReactInputValue(input, partnerSearchTerm);

  log?.('Waiting for partner search API (polling for dropdown options)...', 'info');
  await new Promise((r) => setTimeout(r, DELAY_INITIAL_BEFORE_POLL));

  const found = await pollUntil(
    () => !!findFirstDropdownOption(),
    DELAY_RANGES_POLL,
    DROPDOWN_OPTIONS_POLL_MAX,
    log,
    'Waiting for options'
  );

  if (!found) {
    log?.('WARNING: Dropdown options not found within timeout, continuing anyway', 'info');
  } else {
    log?.('Partner search API responded, options ready', 'success');
  }
}


function clickOption(option: HTMLElement): void {
  option.scrollIntoView({ block: 'nearest' });
  // react-select selects on mousedown, not click
  option.dispatchEvent(
    new MouseEvent('mousedown', {
      bubbles: true,
      cancelable: true,
      view: window,
      buttons: 1,
      clientX: option.getBoundingClientRect().left,
      clientY: option.getBoundingClientRect().top,
    })
  );
}

async function selectFirstDropdownItem(log?: LogFn): Promise<void> {
  log?.('Selecting first dropdown item...', 'info');

  const inputElement =
    findPartnerInput() ||
    ReactSelectHelpers.findAnyReactSelectInput() ||
    ReactSelectHelpers.findInputById('partner-select-input');

  if (!inputElement) {
    log?.('ERROR: Input element not found for dropdown selection', 'error');
    throw new Error('Input element not found for dropdown selection');
  }

  await new Promise((r) => setTimeout(r, DELAY_INITIAL_BEFORE_POLL));

  inputElement.focus();
  await new Promise((r) => setTimeout(r, 80)); // Let focus settle

  // 1) Enter first: option is already highlighted when we type
  // react-select listens on the control div, not the input
  log?.('Pressing Enter (option already focused)...', 'info');
  ReactSelectHelpers.simulateEnter(inputElement);
  await new Promise((r) => setTimeout(r, 300));

  const dropdownClosed = !document.querySelector('[role="listbox"]');
  if (dropdownClosed) {
    log?.('Selected via Enter', 'success');
    await new Promise((r) => setTimeout(r, DELAY_STEPS));
    return;
  }

  // 2) ArrowDown + Enter (in case focus was not on first option)
  log?.('Trying ArrowDown + Enter...', 'info');
  await ReactSelectHelpers.simulateKeyboardSelection(inputElement);
  await new Promise((r) => setTimeout(r, 300));

  if (!document.querySelector('[role="listbox"]')) {
    log?.('Selected via keyboard', 'success');
    await new Promise((r) => setTimeout(r, DELAY_STEPS));
    return;
  }

  // 3) Click option (fallback)
  const option = findFirstDropdownOption();
  if (option) {
    log?.('Clicking option (fallback)...', 'info');
    clickOption(option);
    await new Promise((r) => setTimeout(r, DELAY_STEPS));
    log?.('Selected via click', 'success');
    return;
  }

  log?.('ERROR: Dropdown selection failed', 'error');
  throw new Error('Dropdown selection failed');
}

/** Expands the Available Ranges view if collapsed (click chevron). Only expand when table is not in DOM. */
function expandAvailableRangesIfNeeded(log?: LogFn): void {
  const expandTable = document.getElementById('available-ranges-expand-table');
  if (!expandTable) {
    const expandBtn = document.querySelector(
      '#available-ranges-table button.badge'
    );
    if (expandBtn) {
      (expandBtn as HTMLElement).click();
      log?.('Expanded Available Ranges view', 'info');
    }
  }
}

/** Finds the first clickable range cell (excludes skeleton placeholder). */
function findRangeCell(): HTMLElement | null {
  return (document.querySelector(
    '#available-ranges-expand-table tbody td.badge.btn-primary.cursor-pointer'
  ) ||
    document.querySelector(
      '#available-ranges-expand-table tbody td.badge.btn-primary'
    )) as HTMLElement | null;
}

function findShareFromInput(): HTMLInputElement | null {
  return document.querySelector(
    'input[id="share-from"]'
  ) as HTMLInputElement | null;
}

function findShareToInput(): HTMLInputElement | null {
  return document.querySelector(
    'input[id="share-to"]'
  ) as HTMLInputElement | null;
}

/**
 * After clicking a range, adjusts shareTo so that shareTo - shareFrom + 1 = sharesToDecrease.
 * E.g. range 1-100, sharesToDecrease 50 → updates share-to input to 50 (range becomes 1-50).
 */
function updateShareToForSharesToDecrease(
  sharesToDecrease: number,
  log?: LogFn
): void {
  const shareFromInput = findShareFromInput();
  const shareToInput = findShareToInput();
  if (!shareFromInput || !shareToInput) {
    log?.(
      'WARNING: share-from or share-to input not found, skipping shareTo adjustment',
      'info'
    );
    return;
  }

  const shareFrom = cleanValue(shareFromInput.value);
  if (Number.isNaN(shareFrom) || shareFrom <= 0) {
    log?.(
      'WARNING: Could not parse shareFrom, skipping shareTo adjustment',
      'info'
    );
    return;
  }

  const newShareTo = shareFrom + sharesToDecrease - 1;
  if (newShareTo < shareFrom) {
    log?.(
      'WARNING: sharesToDecrease would produce invalid range, skipping',
      'info'
    );
    return;
  }

  setReactInputValue(shareToInput, String(newShareTo));
  shareToInput.dispatchEvent(new Event('change', { bubbles: true }));
  log?.(
    `Adjusted shareTo to ${newShareTo} (shareFrom ${shareFrom} + sharesToDecrease ${sharesToDecrease} - 1)`,
    'success'
  );
}

/**
 * Clicks the first (and expected only) range in Rangos disponibles.
 * Polls until the range appears (API load) or max attempts reached.
 * Each participant should have exactly 1 range.
 */
async function clickFirstAvailableRange(log?: LogFn): Promise<void> {
  log?.('Clicking first available range...', 'info');
  expandAvailableRangesIfNeeded(log);
  await new Promise((r) => setTimeout(r, 200)); // Let expanded table render if we just expanded

  const pollForRange = (): Promise<HTMLElement> =>
    new Promise((resolve, reject) => {
      let attempts = 0;

      const tryFind = () => {
        const rangeCell = findRangeCell();
        if (rangeCell) {
          resolve(rangeCell);
          return;
        }
        attempts += 1;
        if (attempts >= RANGES_POLL_MAX_ATTEMPTS) {
          reject(
            new Error(
              'No available range found. Ensure partner and class are selected and ranges have loaded.'
            )
          );
          return;
        }
        log?.(
          `Range not found yet, retrying (${attempts}/${RANGES_POLL_MAX_ATTEMPTS})...`,
          'info'
        );
        setTimeout(tryFind, DELAY_RANGES_POLL);
      };

      setTimeout(tryFind, 100); // Brief delay for first poll
    });

  try {
    const rangeCell = await pollForRange();
    rangeCell.click();
    log?.('Clicked first available range', 'success');
    await new Promise((r) => setTimeout(r, DELAY_AFTER_RANGE_CLICK));
  } catch (error) {
    log?.(
      'ERROR: No available range found. Ensure partner and class are selected.',
      'error'
    );
    throw error;
  }
}

function getSaveButton(): HTMLElement | null {
  return (
    document.getElementById('save-movement-button') ||
    (Array.from(document.querySelectorAll('button')).find((btn) =>
      btn.textContent?.match(/Guardar movimiento|Save Movement/i)
    ) as HTMLElement | null)
  );
}

/** Waits until save button is enabled (validation API completed). Returns true if enabled. */
async function waitForSaveButtonEnabled(log?: LogFn): Promise<boolean> {
  return pollUntil(
    () => {
      const btn = getSaveButton();
      return !!btn && !btn.hasAttribute('disabled');
    },
    SAVE_BUTTON_POLL_INTERVAL,
    SAVE_BUTTON_POLL_MAX,
    log,
    'Validation API'
  );
}

function findValidateRangeButton(): HTMLButtonElement | null {
  return Array.from(document.querySelectorAll('button')).find(
    (btn) =>
      btn.textContent?.includes('Validar rango') ||
      btn.textContent?.includes('Validate range') ||
      btn.textContent?.includes('Validate Range')
  ) as HTMLButtonElement | null;
}

async function clickValidateRangeButton(log?: LogFn): Promise<void> {
  log?.('Clicking Validate Range button...', 'info');
  await new Promise((r) => setTimeout(r, DELAY_STEPS));

  let validateButton = findValidateRangeButton();
  if (!validateButton) {
    log?.('ERROR: Validate range button not found', 'error');
    throw new Error('Validate range button not found');
  }

  let attempts = 0;
  while (validateButton?.disabled && attempts < VALIDATE_BUTTON_POLL_MAX) {
    attempts += 1;
    log?.(
      `Validate button disabled, waiting for range selection (${attempts}/${VALIDATE_BUTTON_POLL_MAX})...`,
      'info'
    );
    await new Promise((r) => setTimeout(r, VALIDATE_BUTTON_POLL_INTERVAL));
    validateButton = findValidateRangeButton();
  }

  const btnToValidate = findValidateRangeButton();
  if (!btnToValidate || btnToValidate.disabled) {
    log?.(
      'ERROR: Validate range button is disabled. Ensure range is selected (shareFrom/shareTo).',
      'error'
    );
    throw new Error(
      'Validate range button is disabled. Range must be selected first (click on available range).'
    );
  }
  btnToValidate.click();
  log?.('Clicked Validate Range. Waiting for validation API...', 'info');
  const validated = await waitForSaveButtonEnabled(log);
  if (validated) {
    log?.('Validation API responded, save button enabled', 'success');
  } else {
    log?.('WARNING: Save button not enabled within timeout', 'info');
  }
}

async function clickSaveMovementButton(log?: LogFn): Promise<void> {
  log?.('Clicking Save Movement button...', 'info');
  await new Promise((r) => setTimeout(r, DELAY_STEPS));

  if (!getSaveButton()) {
    log?.('ERROR: Save movement button not found', 'error');
    throw new Error('Save movement button not found');
  }

  await waitForSaveButtonEnabled(log);
  const btnToClick = getSaveButton();
  if (!btnToClick || btnToClick.hasAttribute('disabled')) {
    log?.(
      'ERROR: Save movement button is disabled. Range must be validated first.',
      'error'
    );
    throw new Error(
      'Save movement button is disabled. Check that range is validated.'
    );
  }

  btnToClick.click();
  log?.('Save Movement step complete', 'success');
  await new Promise((r) => setTimeout(r, DELAY_STEPS));
}

async function runScripturePhase(
  date: string,
  totalSharesToDecrease: number,
  log?: LogFn
): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      log?.('Scripture phase: Filling date and total shares...', 'info');
      const dateInput = document.querySelector(
        'input[type="date"], input[id="date"]'
      ) as HTMLInputElement;
      const sharesInput = document.getElementById(
        'shares-to-decrease'
      ) as HTMLInputElement;

      if (!dateInput || !sharesInput) {
        log?.('ERROR: Scripture view inputs not found', 'error');
        throw new Error('Scripture view inputs not found');
      }

      dateInput.value = date;
      dateInput.dispatchEvent(new Event('input', { bubbles: true }));
      dateInput.dispatchEvent(new Event('change', { bubbles: true }));

      sharesInput.value = String(totalSharesToDecrease);
      sharesInput.dispatchEvent(new Event('input', { bubbles: true }));
      sharesInput.dispatchEvent(new Event('change', { bubbles: true }));

      log?.(
        `Filled date=${date}, totalShares=${totalSharesToDecrease}`,
        'info'
      );

      setTimeout(() => {
        log?.('Clicking Next button...', 'info');
        const nextButton = Array.from(document.querySelectorAll('button')).find(
          (btn) =>
            btn.textContent?.includes('Siguiente') ||
            btn.textContent?.includes('Next')
        );

        if (!nextButton || nextButton.hasAttribute('disabled')) {
          log?.('ERROR: Next button not found or disabled', 'error');
          reject(new Error('Next button not found or disabled'));
          return;
        }

        nextButton.click();
        log?.('Scripture phase complete', 'success');
        setTimeout(() => resolve(), DELAY_SCRIPTURE_AFTER_NEXT);
      }, DELAY_STEPS);
    } catch (error) {
      log?.(`ERROR: ${(error as Error).message}`, 'error');
      reject(error);
    }
  });
}

/**
 * Process a single participant: select partner, click first available range, validate, save.
 * Class is assumed already selected (via defaultClassSearchTerm) or must be selected manually before.
 * Each step completes before the next starts.
 */
export async function automateDecreaseParticipant(
  participant: DecreaseParticipant,
  steps: {
    skipTypeInPartnerInput?: boolean;
    skipSelectPartnerDropdown?: boolean;
    skipClickFirstRange?: boolean;
    skipValidateRange?: boolean;
    skipSaveMovement?: boolean;
  } = {},
  log?: LogFn,
  callbacks?: {
    onMovementSaved?: (
      participant: DecreaseParticipant // eslint-disable-line no-unused-vars
    ) => void;
  }
): Promise<void> {
  log?.(`--- Participant: ${participant.partnerSearchTerm} ---`, 'info');

  if (!steps.skipTypeInPartnerInput) {
    await typeInPartnerInput(participant.partnerSearchTerm, log);
  }

  if (!steps.skipSelectPartnerDropdown) {
    await selectFirstDropdownItem(log);
  }

  if (!steps.skipClickFirstRange) {
    log?.('Waiting for ranges API (polling)...', 'info');
    await new Promise((r) => setTimeout(r, DELAY_RANGES_INITIAL));
    await clickFirstAvailableRange(log);

    if (
      participant.sharesToDecrease != null &&
      participant.sharesToDecrease > 0
    ) {
      log?.(
        `Adjusting shareTo for sharesToDecrease=${participant.sharesToDecrease}...`,
        'info'
      );
      await new Promise((r) => setTimeout(r, 100)); // Let React update shareFrom/shareTo
      updateShareToForSharesToDecrease(participant.sharesToDecrease, log);
      await new Promise((r) => setTimeout(r, DELAY_AFTER_RANGE_CLICK));
    }
  }

  if (!steps.skipValidateRange) {
    await clickValidateRangeButton(log);
  }

  if (!steps.skipSaveMovement) {
    await clickSaveMovementButton(log);
    callbacks?.onMovementSaved?.(participant);
  }

  log?.(`Participant complete: ${participant.partnerSearchTerm}`, 'success');
}

/**
 * Run the full automation for all participants.
 * Stops after creating all movements - user reviews and clicks "Guardar operación" manually.
 *
 * Flow:
 * 1. (Optional) Scripture phase: date, total shares, Next
 * 2. (Optional) Select class once if defaultClassSearchTerm is set
 * 3. For each participant: search partner → select → click first range → validate → save
 */
export async function automateDecreaseOperation(
  participants: DecreaseParticipant[],
  config: DecreaseConfig = {}
): Promise<void> {
  const {
    runScripturePhase: doScripture = false,
    date,
    totalSharesToDecrease,
    defaultClassSearchTerm,
    onLog,
    onMovementSaved,
  } = config;

  const log: LogFn = (msg, type = 'info') => onLog?.({ msg, type });

  log('Automation started', 'info');

  if (doScripture) {
    if (!date || !totalSharesToDecrease) {
      log(
        'ERROR: date and totalSharesToDecrease required when runScripturePhase is true',
        'error'
      );
      throw new Error(
        'date and totalSharesToDecrease are required when runScripturePhase is true'
      );
    }
    await runScripturePhase(date, totalSharesToDecrease, log);
  }

  if (defaultClassSearchTerm) {
    log(
      `Class "${defaultClassSearchTerm}" is pre-selected via config (options filtered). Skipping class selection step.`,
      'info'
    );
  }

  for (let i = 0; i < participants.length; i += 1) {
    log(`\n--- Participant ${i + 1}/${participants.length} ---`, 'info');
    const index = i + 1;
    await automateDecreaseParticipant(participants[i], {}, log, {
      onMovementSaved: onMovementSaved
        ? (p) => onMovementSaved(p, index)
        : undefined,
    });
  }

  log(
    `Automation complete. ${participants.length} movement(s) created. Review and click "Guardar operación" to save.`,
    'success'
  );
}
